import { Link, useParams } from "react-router-dom"
import { InlineEditableBooleanProperty } from "../../../components/form/InlineEditableBooleanProperty"
import { InlineEditableProperty } from "../../../components/form/InlineEditableProperty"
import { QueryRender } from "../../../components/helpers/QueryRender"
import { UserAvatar } from "../../../components/user/UserAvatar"
import { UserRoleBadge } from "../../../components/user/UserRoleBadge"
import { useGlobalSettings } from "../../../entities/globalSettings/useGlobalSettings"
import { firstNameSchema, lastNameSchema, phoneSchema } from "../../../entities/user/user"
import { useUpdateUser, useUser } from "../../../entities/user/useUser"
import { uploadPhoto, getImageFileFromUrl, getImageFileFromUrlFallback } from "../../../firebase/storage"
import { formatPhoneNumber } from "@/utils/formatPhoneNumber"
import { YoungContactsForm } from "@/components/user/YoungContactsForm"
import { useProcess } from "@/contexts/ProcessContext"
import { isDevEnv } from "@/config/env"

export default function UserDetails() {
   const { id } = useParams<{ id: string }>()
   const { data: user, ...query } = useUser({ id })
   const updateUser = useUpdateUser()
   const globalSettingsQuery = useGlobalSettings()
   const { startProcess } = useProcess()


   const handlePropertySave = async (field: string, value: any) => {
      if (!user) return
      await updateUser(user.id, { [field]: value })
   }

   const handlePhotoChange = async (file: File) => {
      if (!user) return
      startProcess(async () => {
         const photoUrl = await uploadPhoto(file)
         await updateUser(user.id, { photo: photoUrl }, { quickMode: true })
      })
   }

   const handleReprocessPhoto = async () => {
      if (!user?.photo) return
      
      startProcess(async () => {
         try {
            // Utiliser la nouvelle fonction getImageFileFromUrl pour obtenir le fichier à partir de l'URL
            let file;
            try {
               file = await getImageFileFromUrl(user.photo);
            } catch (error) {
               console.log("Première méthode échouée, tentative avec la méthode de secours...", error);
               // Si getImageFileFromUrl échoue, utiliser getImageFileFromUrlFallback
               file = await getImageFileFromUrlFallback(user.photo);
            }
            
            // Vérifier si nous avons réussi à obtenir un fichier
            if (!file) {
               throw new Error("Impossible de récupérer la photo de profil");
            }
            
            // Appeler directement handlePhotoChange avec le fichier
            handlePhotoChange(file);
         } catch (error) {
            console.error("Erreur lors du retraitement de la photo:", error);
            
            // Afficher un message d'erreur plus convivial à l'utilisateur
            alert("Impossible de retraiter la photo de profil. Vous pouvez essayer de télécharger manuellement une nouvelle photo en cliquant sur l'avatar.");
            
            // Ouvrir l'image dans un nouvel onglet pour que l'utilisateur puisse la télécharger manuellement
            window.open(user.photo, '_blank');
         }
      });
   }

   return (
      <div className="page-section max-w-xl">
         <QueryRender query={query} data={user} emptyMessage="Utilisateur non trouvé">
            {(user) => (
               <div className="">
                  
                  {isDevEnv && user.photo && (
                     <div className="flex justify-center mb-4">
                        <button 
                           className="btn btn-primary btn-sm"
                           onClick={handleReprocessPhoto}
                        >
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                           </svg>
                           DEV: Retraiter la photo de profil
                        </button>
                     </div>
                  )}

                  <div className="flex items-center justify-center space-x-4 mb-8">
                     <UserAvatar
                        photo={user.photo}
                        name={`${user.firstName} ${user.lastName}`}
                        size="xl"
                        editable
                        onPhotoChange={handlePhotoChange}
                     />
                     <div className="flex flex-col gap-3">
                        <div className="text-2xl font-bold">
                           {user.firstName} {user.lastName}
                        </div>
                        <div className="flex items-center justify-center gap-2">
                           <UserRoleBadge role={user.role} size="lg" />
                           <button
                              className="btn  btn-sm "
                              onClick={() =>
                                 handlePropertySave(
                                    "role",
                                    user.role === "admin" ? "young" : "admin",
                                 )
                              }
                           >
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 fill="none"
                                 viewBox="0 0 24 24"
                                 strokeWidth="1.5"
                                 stroke="currentColor"
                                 className="size-6"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                 />
                              </svg>
                           </button>
                        </div>
                        <Link to={`/admin/resume-du-jeune/${user.id}`}
                           className="btn btn-sm _btn-ghost"
                        >
                           Aller au Résumé
                        </Link>
                     </div>
                  </div>

                  <div className=" ">
                     <div className="">
                        <div className=" ">
                           <InlineEditableProperty
                              label="Email"
                              value={user?.email}
                              field="email"
                              editable={false}
                           />

                           <InlineEditableProperty
                              label="Prénom"
                              value={user?.firstName}
                              field="firstName"
                              schema={firstNameSchema}
                              onSave={handlePropertySave}
                           />

                           <InlineEditableProperty
                              label="Nom"
                              value={user?.lastName}
                              field="lastName"
                              schema={lastNameSchema}
                              onSave={handlePropertySave}
                           />

                           <InlineEditableProperty
                              label="Téléphone"
                              value={user?.phone}
                              field="phone"
                              formatDisplayedValue={formatPhoneNumber}
                              schema={phoneSchema}
                              onSave={handlePropertySave}
                           />

                           {user.role === "young" && (
                              <QueryRender query={globalSettingsQuery}>
                                 {(settings) => (
                                    <InlineEditableProperty
                                       label="Groupe"
                                       value={user?.group}
                                       field="group"
                                       type="select"
                                       options={settings.groups}
                                       onSave={handlePropertySave}
                                    />
                                 )}
                              </QueryRender>
                           )}
                        </div>

                        {user.role === "young" && (
                           <YoungContactsForm user={user} onPropertySave={handlePropertySave} />
                        )}

                        <div className="divider mt-12">Préférences de notification</div>
                        <div className="grid grid-cols-1 gap-4">
                           <InlineEditableBooleanProperty
                              label="Notifications Email"
                              value={user.notificationPreferences.email}
                              field="notificationPreferences.email"
                              onSave={(field, value) =>
                                 handlePropertySave("notificationPreferences", {
                                    ...user.notificationPreferences,
                                    email: value,
                                 })
                              }
                           />
                           <InlineEditableBooleanProperty
                              label="Notifications SMS"
                              value={user.notificationPreferences.sms}
                              field="notificationPreferences.sms"
                              onSave={(field, value) =>
                                 handlePropertySave("notificationPreferences", {
                                    ...user.notificationPreferences,
                                    sms: value,
                                 })
                              }
                           />
                        </div>

                        <div className="divider"></div>
                        <div className="flex justify-center">
                           <button
                              className="btn btn-error"
                              onClick={() => handlePropertySave("is_active", !user.is_active)}
                           >
                              {user.is_active ? "Désactiver" : "Réactiver"} l'utilisateur
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            )}
         </QueryRender>
      </div>
   )
}
